/*******************************************************************************
  설  명 : 홈페이지 > 회원정보수정
  작성자 : 송영석
  작성일 : 2020-06-08
*******************************************************************************/
import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';

import { ApiResponse } from '@app/service/api-response';
import { UtilService } from '@app/service/util.service';
import { CommonService } from '@app/service/common.service';

import { HMemberService } from '@home/service/member.service';
import { SystemService } from '@admin/service/system.service';

import { O2OZCMS } from '@app/components/o2oz-cms/o2oz-cms.component';

import * as moment from 'moment';

@Component({
  selector: 'app-member-edit1',
  templateUrl: './member-edit1.component.html',
  styleUrls: ['./member-edit1.component.scss']
})
export class MemberEdit1Component extends O2OZCMS implements OnInit {

  /*******************************************************************************
    설명 : 전역 변수 선언부
  *******************************************************************************/
  errorResponse: ApiResponse;
  public form: FormGroup;
  public formErrors = {};

  public checkTrue = true;

  daumAddressOptions =  {
    class: ['btn', 'zip-btn']
  };

  /** 담당자 관련변수
   * @param companySeq: 회사순번 <br/>
   * @param memberSeq: 회원순번
   * @param isMajor: 주담당자여부
   * @param maxMemberCount: 기업별 최대 담당자수
   * @param currentCount: 현재 가입 직원
   * @param expire: 유료회원 만료여부
   */
  public manager = {
    companySeq: '',
    memberSeq: '',
    isMajor: false,
    maxMemberCount: '',
    currentCount: '',
    expire: false,
  };

  /* 기업아이디 리스트 */
  public memberList: any = [];


  /*******************************************************************************
    설  명 : 생성자
    입력값 : 없음
    리턴값 : 없음
  *******************************************************************************/
  constructor(
    router: Router,
    commonService: CommonService,
    activatedRoute: ActivatedRoute,
    toastrService: ToastrService,
    private utilService: UtilService,
    private formBuilder: FormBuilder,
    private memberService: HMemberService,
    private systemService: SystemService,
  ) {
    // 상위 클래스에 router 전달
    super(router, commonService, activatedRoute, toastrService);

    this.buildForm();
  }

  /*******************************************************************************
    설  명 : 빌드폼 생성
    입력값 : 없음
    리턴값 : 없음
  *******************************************************************************/
  buildForm(): void {
    this.form = this.formBuilder.group({
      memNo: [ '', [Validators.required] ],
      userId: [ '', [Validators.required] ],
      password: [ '', [Validators.pattern('^(?=.*[a-zA-Z])(?=.*[0-9])[A-Za-z0-9\d].{5,19}$')] ],
      passwordConfirm: [ '', [] ],
      userName: [ '', [Validators.required, Validators.pattern('^[가-힣a-zA-Z]*$')] ],
      birth: [ '', [Validators.required] ],
      userEname: [ '', [] ],
      companySeq: [ '', []],
      companySeqName: [ '', []],
      comNumber: [ '', []],
      companyName: [ '', [] ],
      comDept: [ '', [] ],
      comJob: [ '', [] ],
      email: [ '', [Validators.required, Validators.pattern('^[a-z0-9._-]+@[a-z0-9.-]+\\.[a-z]{2,4}$')] ],
      emailDupCheck:[ true, [Validators.required] ],
      telephone1: [ '', [] ],
      telephone2: [ '', [] ],
      telephone3: [ '', [] ],
      mobile1: [ '', [] ],
      mobile2: [ '', [] ],
      mobile3: [ '', [] ],
      zipcode: [ '', [] ],
      address: [ '', [] ],
      addressDetail: [ '', [] ],
      smsCheck: [ true, [] ],
      emailCheck: [ true, [] ],
      effectiveSdate: [ '', [] ],
      effectiveEdate: [ '', [] ],
    });

    this.form.valueChanges.subscribe(data => {
      this.utilService.updateFormErrors( this.form, this.formErrors );
    });
  }

  async ngOnInit() {
    // 시스템 설정
    this.getSystemConfig();

    // 회원 정보 가져오기
    await this.getMemberDetail();

    if ( this.form.controls.companySeq.value !== null ) {
      this.form.get('telephone1').clearValidators();
      this.form.get('telephone1').updateValueAndValidity();
      this.form.get('telephone2').clearValidators();
      this.form.get('telephone2').updateValueAndValidity();
      this.form.get('telephone3').clearValidators();
      this.form.get('telephone3').updateValueAndValidity();
    }
  }

  /*******************************************************************************
    설  명 : 시스템 설정 가져오기 (기업별 담당자수)
  *******************************************************************************/
  getSystemConfig() {
    this.systemService.getSystemConfig().then( response => {
      if ( response.ResultCode ) {
        for ( const item of response.data ) {
          if ( item.common_code === '3000' ) {
            this.manager.maxMemberCount = item.c1;
          }
        }
      }
    }).catch( error => {
      this.toastrService.error(error, '시스템설정');
    });
  }

  /*******************************************************************************
    설  명 : 회원 정보 가져오기
    입력값 : 없음
    리턴값 : 없음
  *******************************************************************************/
  async getMemberDetail() {
    await this.memberService.getMemberDetail().then( response => {
      if ( response.ResultCode ) {

        const memberInfo = response.data;
        memberInfo.password = '';

        if ( memberInfo.mobile !== null ) {
          if ( memberInfo.mobile.indexOf('-') < 0 ) {
            // 전화번호 하이픈 처리
            memberInfo.mobile = memberInfo.mobile.replace(/(^02.{0}|^01.{1}|[0-9]{3})([0-9]+)([0-9]{4})/, '$1-$2-$3');
          }
          const mobile = memberInfo.mobile.split('-');
          memberInfo.mobile1 = mobile[0];
          memberInfo.mobile2 = mobile[1];
          memberInfo.mobile3 = mobile[2];
        }

        if( memberInfo.telephone !== null ) {
          if ( memberInfo.telephone.indexOf('-') < 0 ) {
            // 전화번호 하이픈 처리
            memberInfo.telephone = memberInfo.telephone.replace(/(^02.{0}|^01.{1}|[0-9]{3})([0-9]+)([0-9]{4})/, '$1-$2-$3');
          }
          const telephone = memberInfo.telephone.split('-');
          memberInfo.telephone1 = telephone[0];
          memberInfo.telephone2 = telephone[1];
          memberInfo.telephone3 = telephone[2];
        }

        memberInfo.smsCheck = ( memberInfo.smsCheck === '1' ) ? true : false;
        memberInfo.emailCheck = ( memberInfo.emailCheck === '1' ) ? true : false;

        this.manager.companySeq = memberInfo.companySeq;
        this.manager.memberSeq = memberInfo.memNo;
        this.manager.isMajor = ( memberInfo.major_yn === '1' ) ? true : false;

        // 기업 담당자
        if (this.manager.isMajor) {
          this.getCompanyMember(this.manager.companySeq);
        }

        // 유료회원 만료여부
        this.manager.expire = moment().isAfter(memberInfo.effectiveEdate, 'day');
        this.form.patchValue( memberInfo );

      } else {
        this.toastrService.error( response.ResultMessage );
      }
    });
  }

  /*******************************************************************************
    설  명 : 기업회원 리스트
    입력값 : 없음
    리턴값 : 없음
  *******************************************************************************/
  getCompanyMember(companySeq) {
    this.memberService.getCompanyMember(companySeq).then( response => {
      if ( response.ResultCode ) {
        // 승인된 기업 아이디 개수 찾기
        this.manager.currentCount = response.data.filter( item => item.approvalStatus === '2000' ).length;

        // 기업아이디 리스트 중 자신은 제거
        const idx = response.data.findIndex(item => {
          return item.memNo === this.manager.memberSeq;
        });
        if (idx > -1) {
          response.data.splice(idx, 1);
        }
        this.memberList = response.data;
      }
    });
  }

  /*******************************************************************************
    설  명 : 이메일 중복 검사
  *******************************************************************************/
  checkEmailChangeDuplicate() {
    this.utilService.makeFieldDirtyAndUpdateErrors(this.form, this.formErrors, 'email');

    if ( this.form.get('email').valid ) {
      this.memberService.checkEmailChangeDuplicate( this.form.get( 'email' ).value ).then( response => {
        if ( response.ResultCode ) {
          this.form.get( 'emailDupCheck' ).setValue( true );
          this.toastrService.success( response.ResultMessage, '이메일 중복체크');
        } else {
          this.form.get( 'emailDupCheck' ).setValue( false );
          this.toastrService.error( response.ResultMessage, '이메일 중복체크');
        }
      });
    } else {
      this.toastrService.error( '필수 입력값을 다시 한번 확인하시기 바랍니다.', '이메일 중복체크');
    }
  }

  /*******************************************************************************
    설  명 : 이메일 변경시 중복 체크 초기화
  *******************************************************************************/
  setEmailDupCheckInit() {
    this.form.get( 'emailDupCheck' ).setValue( false );
  }

  /*******************************************************************************
    설  명 : 다음 주소 처리
  *******************************************************************************/
 setDaumAddressApi(data) {
    // 여기로 주소값이 반환
    this.form.patchValue({
      zipcode: data.zip,
      address: data.addr
    });

    $("#addressDetail").focus();
  }

  /*******************************************************************************
    설  명 : 기업아이디 승인/취소처리
  *******************************************************************************/
  async setCompanyPersonApproval(memNo, isConfirm) {
    const memberSeq: any = [{memberSeq: memNo}];

    if (isConfirm) {
      // 기업별 담당자 수 확인
      if ( this.manager.currentCount >= this.manager.maxMemberCount ) {
        this.toastrService.warning( '기업별 담당자 수를 초과 하였습니다.');
        return false;
      }

      await this.memberService.setCompanyPersonApproval( memberSeq ).then( response => {
        if ( response.ResultCode ) {
          this.toastrService.success( response.ResultMessage, '');
          this.getCompanyMember(this.manager.companySeq);
        } else {
          this.toastrService.error( response.ResultMessage, '');
        }
      });
    } else {
      await this.memberService.setCompanyPersonApprovalCancel( memberSeq ).then( response => {
        if ( response.ResultCode ) {
          this.toastrService.success( response.ResultMessage, '');
          this.getCompanyMember(this.manager.companySeq);
        } else {
          this.toastrService.error( response.ResultMessage, '');
        }
      });
    }
  }

  /*******************************************************************************
    설  명 : 회원정보수정 처리
  *******************************************************************************/
  public setMemberSave() {
    this.utilService.makeFormDirtyAndUpdateErrors(this.form, this.formErrors);

    if ( this.form.valid ) {
      this.memberService.setMemberSave( this.form ).then(response => {
        if ( response.ResultCode == true ) {
          this.toastrService.success(response.ResultMessage, '회원정보수정');

          this.router.navigate(['/main']);
        } else {
          this.toastrService.error(response.ResultMessage, '회원정보수정');
        }
      })
      .catch(response => {
        this.errorResponse = response;
        this.utilService.handleFormSubmitError(this.errorResponse, this.form, this.formErrors);
      });
    } else {
      this.toastrService.error( '필수 입력값을 다시 한번 확인하시기 바랍니다.', '회원정보수정');
    }
  }

  /*******************************************************************************
    설  명 : 주 담당자 지정
  *******************************************************************************/
  public setCompanyMajor(memberSeq) {
    if ( confirm('주 담당자를 변경하시겠습니까?') ) {
      this.memberService.setCompanyMajor( this.manager.companySeq, memberSeq ).then(response => {
        if ( response.ResultCode === true ) {
          this.toastrService.success(response.ResultMessage, '주 담당자 지정');
          this.getMemberDetail();
        } else {
          this.toastrService.error(response.ResultMessage, '주 담당자 지정');
        }
      })
      .catch(response => {
        this.errorResponse = response;
        this.utilService.handleFormSubmitError(this.errorResponse, this.form, this.formErrors);
      });
    }
  }
}
